import {
  PIPELINE_PARENT,
  PIPELINE_TYPES,
  PIPELINE_TYPE_META,
  allowsSeveralPipelines,
  allowsTriggers,
  type PipelineType,
  type TerminalKind,
} from '@crm/contracts';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type KeyboardCoordinateGetter,
} from '@dnd-kit/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  GripVertical,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { StageChip } from '../components/pipelines/stage-chip.tsx';
import { SHADE_DOT } from '../components/pipelines/stage-colours.ts';
import { StageAutomationsButton } from '../components/pipelines/stage-automations.tsx';
import { Gate } from '../components/shell/gate.tsx';
import { Badge } from '../components/ui/badge.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu.tsx';
import { Input } from '../components/ui/input.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.tsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs.tsx';
import { describeError } from '../lib/api.ts';
import { api, type PipelineStageView, type PipelineView } from '../lib/client.ts';
import { moveStage } from '../lib/pipelines.ts';
import { useSession } from '../lib/session.tsx';
import { cn } from '../lib/utils.ts';
import { PageHeader } from '../components/ui/page-header.tsx';

/**
 * Settings → Pipelines (RELAY-948): the stage lists every record type moves through — Sales
 * Flow, Deal Flow, Job Flow, Shortlist Flow, Candidate Flow, one tab per type. Everyone sees them;
 * organisation admins rename, add, remove and drag stages into order. A stage keeps
 * its id and slug whatever it is renamed to, so nothing that points at it breaks.
 *
 * TS-1746: a type that allows several pipelines (projects — Sales, Upsell/Crossell…) shows its
 * live pipelines side by side, as compact columns in a horizontally scrolling row (the default
 * first), with "New pipeline" above them and Archive on each but the default. Names that do not
 * fit are cut with an ellipsis and keep the whole of them in `title`. Archived pipelines leave
 * the row for the "Archived" tab: a plain list of every archived pipeline, each with Unarchive,
 * narrowed in the browser by name and type (the list is already loaded whole).
 * This page reads archived pipelines too, under its own query key, so every other page's
 * `['pipelines']` stays live pipelines only.
 */
const PIPELINES_QUERY = ['pipelines'] as const;
const SETTINGS_QUERY = ['pipelines', 'settings'] as const;
const RULES_QUERY = ['pipelines', 'rollup-rules'] as const;
const COUNTS_QUERY = ['pipelines', 'stage-counts'] as const;
const ARCHIVED_TAB = 'archived';

export function PipelinesPage() {
  const session = useSession();
  const canManage = session.can('organisation.manage');
  const pipelines = useQuery({
    queryKey: SETTINGS_QUERY,
    queryFn: () => api.pipelines.list({ includeArchived: true }),
  });
  // Under the pipelines key, so a change that re-reads pipelines re-reads the counts too.
  const stageCounts = useQuery({
    queryKey: COUNTS_QUERY,
    queryFn: () => api.pipelines.stageCounts(),
    select: (rows) => new Map(rows.map((r) => [r.stageId, r.count])),
  });
  const all = pipelines.data ?? [];
  const live = all.filter((p) => !p.archivedAt);
  const archived = all.filter((p) => p.archivedAt);
  const types = PIPELINE_TYPES.filter((t) => live.some((p) => p.appliesTo === t));
  // Roll-up rules point at each type's default pipeline (the one a record's stage lives in).
  const defaults = live.filter((p) => p.isDefault);
  // Only a type that allows several pipelines can archive one, so only then is there a tab.
  const archiving = types.some(allowsSeveralPipelines);
  return (
    <Gate permission="organisation.read">
      <div>
        <PageHeader
          title="Pipelines"
          description={
            <>
              The stages projects, deals, jobs, shortlisted candidates and candidates move through.
              Renaming a stage keeps everything that points at it.
              {!canManage && ' Only an organisation admin can change them.'}
            </>
          }
        />
        {pipelines.isError && (
          <p className="text-danger text-sm" role="alert">
            {describeError(pipelines.error)}
          </p>
        )}
        {types.length > 0 && (
          <Tabs defaultValue={types[0]!}>
            <TabsList className="h-auto flex-wrap">
              {types.map((t) => (
                <TabsTrigger key={t} value={t}>
                  <span
                    className={cn(
                      'mr-1.5 size-2 rounded-full',
                      SHADE_DOT[PIPELINE_TYPE_META[t].family]['500'],
                    )}
                  />
                  {PIPELINE_TYPE_META[t].label}
                </TabsTrigger>
              ))}
              {archiving && (
                <TabsTrigger value={ARCHIVED_TAB}>Archived ({archived.length})</TabsTrigger>
              )}
            </TabsList>
            {types.map((t) => (
              <TabsContent key={t} value={t} className="mt-4 flex min-w-0 flex-col gap-6">
                <TypeTab
                  type={t}
                  pipelines={live.filter((p) => p.appliesTo === t)}
                  defaults={defaults}
                  canManage={canManage}
                  rollups={session.isEnabled('pipelines.rollup_rules')}
                  counts={stageCounts.data}
                />
              </TabsContent>
            ))}
            {archiving && (
              <TabsContent value={ARCHIVED_TAB} className="mt-4">
                <ArchivedPipelines pipelines={archived} canManage={canManage} />
              </TabsContent>
            )}
          </Tabs>
        )}
      </div>
    </Gate>
  );
}

/**
 * One type's tab. A type with one pipeline shows its editor as before; a type that allows
 * several shows every live one side by side (default first), each a compact column, in a row
 * that scrolls sideways rather than wrapping — so a pipeline's stages always read top to bottom.
 */
function TypeTab({
  type,
  pipelines,
  defaults,
  canManage,
  rollups,
  counts,
}: {
  type: PipelineType;
  /** The type's live pipelines. */
  pipelines: PipelineView[];
  defaults: PipelineView[];
  canManage: boolean;
  rollups: boolean;
  counts: ReadonlyMap<string, number> | undefined;
}) {
  const several = allowsSeveralPipelines(type);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<string | null>(null);
  const byDefault = [...pipelines].sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
  const main = byDefault[0]!;
  const rules = rollups && PIPELINE_PARENT[type] && (
    <RollupRules pipeline={main} pipelines={defaults} canManage={canManage} />
  );
  if (!several)
    return (
      <div className="flex max-w-3xl flex-col gap-6">
        <PipelineEditor pipeline={main} canManage={canManage} counts={counts} />
        {rules}
      </div>
    );
  return (
    <>
      {canManage && (
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setCreating(true)}>
            <Plus /> New pipeline
          </Button>
        </div>
      )}
      <div
        className="-mx-1 flex items-start gap-4 overflow-x-auto px-1 pb-3"
        data-testid={`pipeline-columns-${type}`}
      >
        {byDefault.map((p) => (
          <PipelineEditor
            key={p.id}
            pipeline={p}
            canManage={canManage}
            several
            compact
            reveal={p.id === created}
            counts={counts}
          />
        ))}
      </div>
      {rules && <div className="max-w-3xl">{rules}</div>}
      {creating && (
        <NewPipelineDialog
          type={type}
          pipelines={pipelines}
          onClose={() => setCreating(false)}
          onCreated={(p) => {
            setCreating(false);
            setCreated(p.id);
          }}
        />
      )}
    </>
  );
}

/** Every archived pipeline, in one list; each can be brought back to its type's tab. */
function ArchivedPipelines({
  pipelines,
  canManage,
}: {
  pipelines: PipelineView[];
  canManage: boolean;
}) {
  const put = usePutPipeline();
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [type, setType] = useState<PipelineType | 'all'>('all');
  const unarchive = useMutation({
    mutationFn: (id: string) => api.pipelines.unarchive(id),
    onSuccess: (next) => {
      setError(null);
      put(next);
    },
    onError: (err) => setError(describeError(err)),
  });
  if (pipelines.length === 0)
    return (
      <p className="text-muted-foreground text-sm" data-testid="archived-pipelines">
        No archived pipelines. Archiving one hides it from pages and boards and keeps its stages and
        history; it is listed here to bring back.
      </p>
    );
  const needle = name.trim().toLowerCase();
  const shown = pipelines.filter(
    (p) =>
      (type === 'all' || p.appliesTo === type) &&
      (!needle || p.name.toLowerCase().includes(needle)),
  );
  return (
    <div className="flex max-w-3xl flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1 sm:max-w-xs">
          <Search
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
            aria-hidden
          />
          <Input
            aria-label="Search archived pipelines"
            placeholder="Search by name…"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="pl-8"
          />
        </div>
        <Select value={type} onValueChange={(v) => setType(v as PipelineType | 'all')}>
          <SelectTrigger aria-label="Type" className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {PIPELINE_TYPES.map((t) => (
              <SelectItem key={t} value={t}>
                {PIPELINE_TYPE_META[t].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {shown.length === 0 && (
        <p className="text-muted-foreground text-sm">No archived pipeline matches.</p>
      )}
      <ul
        className="border-border divide-border bg-card shadow-card rounded-card divide-y border empty:hidden"
        data-testid="archived-pipelines"
      >
        {shown.map((p) => (
          <li
            key={p.id}
            className="flex min-w-0 items-center gap-3 px-4 py-3 text-sm"
            data-pipeline-id={p.id}
          >
            <span
              className={cn(
                'size-2 shrink-0 rounded-full',
                SHADE_DOT[PIPELINE_TYPE_META[p.appliesTo].family]['500'],
              )}
            />
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate font-medium" title={p.name}>
                {p.name}
              </span>
              <span className="text-muted-foreground truncate text-xs">
                {PIPELINE_TYPE_META[p.appliesTo].label} · {p.stages.length} stages · archived{' '}
                {new Date(p.archivedAt!).toLocaleDateString()}
              </span>
            </div>
            {canManage && (
              <Button
                size="sm"
                variant="outline"
                aria-label={`Unarchive ${p.name}`}
                disabled={unarchive.isPending}
                onClick={() => unarchive.mutate(p.id)}
              >
                <ArchiveRestore /> Unarchive
              </Button>
            )}
          </li>
        ))}
      </ul>
      {error && (
        <p className="text-danger text-sm" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Keep both caches right after a change: this page's list (archived included) gets the
 * pipeline the API returned, and every other page's live list is re-read.
 */
function usePutPipeline() {
  const qc = useQueryClient();
  return (next: PipelineView) => {
    qc.setQueryData<PipelineView[]>(SETTINGS_QUERY, (old) =>
      old?.some((p) => p.id === next.id)
        ? old.map((p) => (p.id === next.id ? next : p))
        : [...(old ?? []), next],
    );
    void qc.invalidateQueries({ queryKey: PIPELINES_QUERY, exact: true });
  };
}

/** A new pipeline of a type that allows several: its name, and the stages it starts with. */
function NewPipelineDialog({
  type,
  pipelines,
  onClose,
  onCreated,
}: {
  type: PipelineType;
  pipelines: PipelineView[];
  onClose: () => void;
  onCreated: (p: PipelineView) => void;
}) {
  const put = usePutPipeline();
  const [name, setName] = useState('');
  // '' = the standard list for the type; otherwise the id of a pipeline to copy the stages of.
  const [from, setFrom] = useState('');
  const [error, setError] = useState<string | null>(null);
  const source = pipelines.find((p) => p.id === from);
  const create = useMutation({
    mutationFn: () =>
      api.pipelines.create({
        appliesTo: type,
        name: name.trim(),
        ...(source && {
          stages: source.stages.map((s) => ({
            name: s.name,
            colour: s.colour,
            isTerminal: s.isTerminal,
            terminalKind: s.terminalKind,
            probability: s.probability,
          })),
        }),
      }),
    onSuccess: (p) => {
      put(p);
      onCreated(p);
    },
    onError: (err) => setError(describeError(err)),
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New {NOUN[type]} pipeline</DialogTitle>
        </DialogHeader>
        <form
          id="pipeline-form"
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <label className="flex flex-col gap-1 text-sm">
            Name
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Upsell/Crossell"
              autoFocus
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Start with
            <Select
              value={from || 'standard'}
              onValueChange={(v) => setFrom(v === 'standard' ? '' : v)}
            >
              <SelectTrigger aria-label="Start with" className="w-72 max-w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">The standard stages</SelectItem>
                {pipelines.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    A copy of {p.name}&rsquo;s stages
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <p className="text-muted-foreground text-xs">
            You can rename, add, remove and reorder its stages once it is created.
          </p>
          {error && (
            <p className="text-danger text-sm" role="alert">
              {error}
            </p>
          )}
        </form>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="pipeline-form" disabled={!name.trim() || create.isPending}>
            Create pipeline
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PipelineEditor({
  pipeline,
  canManage,
  several = false,
  compact = false,
  reveal = false,
  counts,
}: {
  pipeline: PipelineView;
  canManage: boolean;
  /** Records per stage (`GET /pipelines/stage-counts`); left out until it has loaded. */
  counts?: ReadonlyMap<string, number> | undefined;
  /** The type may have several pipelines: say which is the default, offer archive. */
  several?: boolean;
  /** A fixed-width column beside others: names cut with an ellipsis, the slug left out. */
  compact?: boolean;
  /** Just created: scroll the row so this column is in view. */
  reveal?: boolean;
}) {
  const qc = useQueryClient();
  const session = useSession();
  const triggersOn = session.can('automation.read') && session.isEnabled('pipelines.triggers');
  const automations = triggersOn && allowsTriggers(pipeline.appliesTo, pipeline.isDefault);
  const put = usePutPipeline();
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<PipelineStageView | 'new' | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [removing, setRemoving] = useState<PipelineStageView | null>(null);
  const fail = (err: unknown) => setError(describeError(err));
  const done = (next: PipelineView) => {
    setError(null);
    put(next);
  };
  // Archiving takes it out of this row and into the Archived tab, where it is brought back.
  const archive = useMutation({
    mutationFn: () => api.pipelines.archive(pipeline.id),
    onSuccess: done,
    onError: fail,
  });
  const revealed = useRef<HTMLElement>(null);
  useEffect(() => {
    // jsdom has no scrollIntoView.
    if (reveal) revealed.current?.scrollIntoView?.({ inline: 'nearest', block: 'nearest' });
  }, [reveal]);

  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.pipelines.reorder(pipeline.id, ids),
    onMutate: (ids) => {
      // Optimistic: the row lands where it was dropped; a refusal puts the server's order back.
      const byId = new Map(pipeline.stages.map((s) => [s.id, s] as const));
      put({ ...pipeline, stages: ids.map((id) => byId.get(id)!) });
    },
    onSuccess: done,
    onError: async (err) => {
      fail(err);
      await qc.invalidateQueries({ queryKey: PIPELINES_QUERY });
    },
  });
  const remove = useMutation({
    mutationFn: (stageId: string) => api.pipelines.removeStage(pipeline.id, stageId),
    onSuccess: async () => {
      setError(null);
      // Prefix: this page's list and every other page's.
      await qc.invalidateQueries({ queryKey: PIPELINES_QUERY });
    },
    onError: fail,
  });
  const rename = useMutation({
    mutationFn: (name: string) => api.pipelines.rename(pipeline.id, name),
    onSuccess: (next) => {
      setRenaming(null);
      done(next);
    },
    onError: fail,
  });

  const ids = pipeline.stages.map((s) => s.id);
  // Every record in the pipeline: each sits in exactly one of its stages.
  const total = counts && ids.reduce((sum, id) => sum + (counts.get(id) ?? 0), 0);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: jumpBetweenRows }),
  );
  const onDragEnd = (e: DragEndEvent) => {
    setDragging(null);
    if (!e.over) return;
    const next = moveStage(ids, String(e.active.id), String(e.over.id));
    if (next.join() !== ids.join()) reorder.mutate(next);
  };
  const stageName = (id: unknown) => pipeline.stages.find((s) => s.id === id)?.name ?? 'nowhere';

  return (
    <section
      ref={revealed}
      className={cn('flex flex-col gap-3', compact && 'w-80 shrink-0')}
      data-testid={`pipeline-${pipeline.appliesTo}`}
      data-pipeline-id={pipeline.id}
      aria-label={pipeline.name}
    >
      {/* One line, whatever the width: the name gives way (ellipsis), the actions never wrap. */}
      <div className="flex min-h-9 min-w-0 items-center gap-2">
        {renaming === null ? (
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <h3 className="min-w-0 truncate font-medium" title={pipeline.name}>
              {pipeline.name}
            </h3>
            {total !== undefined && (
              <span
                className={cn(
                  'bg-muted text-muted-foreground shrink-0 rounded-full px-2 py-0.5 text-xs tabular-nums',
                  total > 0 && 'text-foreground',
                )}
                title={`${countLabel(pipeline.appliesTo, total)} in ${pipeline.name}`}
                data-testid="pipeline-count"
              >
                {total}
              </span>
            )}
            {several && pipeline.isDefault && (
              <Badge variant="secondary" className="shrink-0">
                Default
              </Badge>
            )}
          </div>
        ) : (
          <form
            className="flex min-w-0 flex-1 items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              rename.mutate(renaming);
            }}
          >
            <Input
              aria-label="Pipeline name"
              value={renaming}
              onChange={(e) => setRenaming(e.target.value)}
              className={compact ? 'min-w-0 flex-1' : 'w-56'}
              autoFocus
            />
            <Button size="sm" type="submit" disabled={!renaming.trim()}>
              Save
            </Button>
            <Button size="sm" variant="ghost" type="button" onClick={() => setRenaming(null)}>
              Cancel
            </Button>
          </form>
        )}
        {canManage && renaming === null && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="size-8 shrink-0"
                aria-label={`Actions for ${pipeline.name}`}
              >
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setEditing('new')}>
                <Plus className="size-4" /> Add stage
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setRenaming(pipeline.name)}>
                <Pencil className="size-4" /> Rename
              </DropdownMenuItem>
              {several && !pipeline.isDefault && (
                <DropdownMenuItem disabled={archive.isPending} onSelect={() => archive.mutate()}>
                  <Archive className="size-4" /> Archive
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={(e) => setDragging(String(e.active.id))}
        onDragEnd={onDragEnd}
        onDragCancel={() => setDragging(null)}
        accessibility={{
          announcements: {
            onDragStart: ({ active }) => `Picked up ${stageName(active.id)}.`,
            onDragOver: ({ active, over }) =>
              `${stageName(active.id)} is over ${stageName(over?.id)}.`,
            onDragEnd: ({ active, over }) =>
              over
                ? `${stageName(active.id)} dropped at ${stageName(over.id)}.`
                : `${stageName(active.id)} was not moved.`,
            onDragCancel: ({ active }) => `Moving ${stageName(active.id)} was cancelled.`,
          },
          screenReaderInstructions: {
            draggable:
              'Press space to pick the stage up, the up and down arrows to choose its place, space to drop it, escape to cancel.',
          },
        }}
      >
        <ol className="border-border divide-border bg-card shadow-card overflow-hidden rounded-card border divide-y">
          {pipeline.stages.map((s) => (
            <StageRow
              key={s.id}
              stage={s}
              canMove={canManage}
              busy={reorder.isPending}
              dragging={dragging === s.id}
              className={cn(
                'flex items-center gap-x-3 gap-y-1 px-3 py-2 text-sm',
                compact ? 'gap-x-1.5 px-2' : 'flex-wrap',
              )}
            >
              <StageChip
                family={pipeline.family}
                colour={s.colour}
                name={s.name}
                terminalKind={s.terminalKind}
                className={compact ? 'shrink' : ''}
              />
              {/* Right beside its stage, so the number reads as that stage's. */}
              {counts && (
                <span
                  className={cn(
                    'bg-muted text-muted-foreground -ml-1 shrink-0 rounded-full px-2 py-0.5 text-xs tabular-nums',
                    compact && '-ml-0.5',
                    (counts.get(s.id) ?? 0) > 0 && 'text-foreground',
                  )}
                  title={`${countLabel(pipeline.appliesTo, counts.get(s.id) ?? 0)} in ${s.name}`}
                  data-testid="stage-count"
                >
                  {counts.get(s.id) ?? 0}
                </span>
              )}
              {!compact && (
                <span className="text-muted-foreground font-mono text-xs">{s.slug}</span>
              )}
              {(canManage || automations) && (
                <span
                  className={cn(
                    'ml-auto flex shrink-0 items-center',
                    compact && '[&_button]:size-7',
                  )}
                >
                  {automations && <StageAutomationsButton pipeline={pipeline} stage={s} />}
                  {canManage && (
                    <>
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Edit ${s.name}`}
                        onClick={() => setEditing(s)}
                      >
                        <Pencil />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Remove ${s.name}`}
                        onClick={() => setRemoving(s)}
                      >
                        <Trash2 />
                      </Button>
                    </>
                  )}
                </span>
              )}
            </StageRow>
          ))}
        </ol>
      </DndContext>
      {triggersOn && !automations && (
        <p className="text-muted-foreground text-xs">
          Automations run on the default Shortlist Flow pipeline.
        </p>
      )}
      {error && (
        <p className="text-danger text-sm" role="alert">
          {error}
        </p>
      )}
      <Dialog open={removing !== null} onOpenChange={(o) => !o && setRemoving(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove {removing?.name}?</DialogTitle>
            <DialogDescription>
              The stage leaves this pipeline and cannot be brought back. It is refused while records
              are in it, a roll-up rule or an automation uses it, or it is the last open stage.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                removing && remove.mutate(removing.id, { onSettled: () => setRemoving(null) })
              }
              disabled={remove.isPending}
              data-testid="confirm-remove-stage"
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {editing && (
        <StageDialog
          pipeline={pipeline}
          stage={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(next) => {
            setEditing(null);
            done(next);
          }}
        />
      )}
    </section>
  );
}

type Ending = 'open' | TerminalKind;

function StageDialog({
  pipeline,
  stage,
  onClose,
  onSaved,
}: {
  pipeline: PipelineView;
  stage: PipelineStageView | null;
  onClose: () => void;
  onSaved: (next: PipelineView) => void;
}) {
  const [name, setName] = useState(stage?.name ?? '');
  const [ending, setEnding] = useState<Ending>(stage?.terminalKind ?? 'open');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => {
      // No colour or probability: an edit keeps the stage's, a new stage takes the API's
      // defaults (the family's middle shade, no probability).
      const body = {
        name: name.trim(),
        isTerminal: ending !== 'open',
        terminalKind: ending === 'open' ? null : ending,
      };
      return stage
        ? api.pipelines.patchStage(pipeline.id, stage.id, body)
        : api.pipelines.addStage(pipeline.id, body);
    },
    onSuccess: onSaved,
    onError: (err) => setError(describeError(err)),
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {stage ? `Edit ${stage.name}` : `Add a stage to ${pipeline.name}`}
          </DialogTitle>
        </DialogHeader>
        <form
          id="stage-form"
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <label className="flex flex-col gap-1 text-sm">
            Name
            <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </label>
          {stage && (
            <p className="text-muted-foreground text-xs">
              Key <span className="font-mono">{stage.slug}</span> stays the same when you rename.
            </p>
          )}
          <label className="flex flex-col gap-1 text-sm">
            Ends the journey?
            <Select value={ending} onValueChange={(v) => setEnding(v as Ending)}>
              <SelectTrigger aria-label="Ends the journey" className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">No — still in progress</SelectItem>
                <SelectItem value="won">Yes — won</SelectItem>
                <SelectItem value="lost">Yes — lost</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <div>
            <span className="text-muted-foreground mr-2 text-xs">Preview</span>
            <StageChip
              family={pipeline.family}
              colour={stage?.colour ?? '500'}
              name={name || 'Stage'}
              terminalKind={ending === 'open' ? null : ending}
            />
          </div>
          {error && (
            <p className="text-danger text-sm" role="alert">
              {error}
            </p>
          )}
        </form>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="stage-form" disabled={!name.trim() || save.isPending}>
            {stage ? 'Save' : 'Add stage'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const PLURAL: Record<PipelineType, string> = {
  project: 'projects',
  deal: 'deals',
  job: 'jobs',
  shortlist: 'shortlisted candidates',
  candidate: 'candidates',
};
const NOUN: Record<PipelineType, string> = {
  project: 'project',
  deal: 'deal',
  job: 'job',
  shortlist: 'shortlisted candidate',
  candidate: 'candidate',
};

/**
 * "3 deals", for a stage count's title (`GET /pipelines/stage-counts`). Nothing moves through
 * Candidate Flow yet: what sits in its stages are shortlist places from before TS-1888, so its
 * counts say shortlisted candidates.
 */
function countLabel(type: PipelineType, n: number): string {
  const counted = type === 'candidate' ? 'shortlist' : type;
  return `${n} ${n === 1 ? NOUN[counted] : PLURAL[counted]}`;
}

/** Roll-up rules from this pipeline to the one above it (flag `pipelines.rollup_rules`). */
function RollupRules({
  pipeline,
  pipelines,
  canManage,
}: {
  pipeline: PipelineView;
  pipelines: PipelineView[];
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const parent = pipelines.find((p) => p.appliesTo === PIPELINE_PARENT[pipeline.appliesTo])!;
  const rules = useQuery({
    queryKey: RULES_QUERY,
    queryFn: () => api.pipelines.rollupRules.list(),
  });
  const [draft, setDraft] = useState<{ source: string; condition: 'any' | 'all'; target: string }>({
    source: '',
    condition: 'all',
    target: '',
  });
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      api.pipelines.rollupRules.create({
        sourceStageId: draft.source,
        condition: draft.condition,
        targetStageId: draft.target,
      }),
    onSuccess: (next) => {
      setError(null);
      setDraft({ source: '', condition: 'all', target: '' });
      qc.setQueryData(RULES_QUERY, next);
    },
    onError: (err) => setError(describeError(err)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.pipelines.rollupRules.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: RULES_QUERY }),
    onError: (err) => setError(describeError(err)),
  });
  const stage = (p: PipelineView, id: string) => p.stages.find((s) => s.id === id);
  const mine = (rules.data ?? []).filter((r) => r.sourcePipeline === pipeline.appliesTo);
  return (
    <section className="flex flex-col gap-2" data-testid="rollup-rules">
      <div>
        <h3 className="font-medium">Roll-up to {parent.name}</h3>
        <p className="text-muted-foreground text-sm">
          Move a {NOUN[parent.appliesTo]} automatically when its {PLURAL[pipeline.appliesTo]} reach
          a stage.
        </p>
      </div>
      {mine.length === 0 && <p className="text-muted-foreground text-sm">No rules yet.</p>}
      <ul className="flex flex-col gap-2">
        {mine.map((r) => {
          const from = stage(pipeline, r.sourceStageId);
          const to = stage(parent, r.targetStageId);
          return (
            <li
              key={r.id}
              className="flex flex-wrap items-center gap-1.5 text-sm"
              data-testid="rollup-rule"
            >
              When {r.condition} of its {PLURAL[pipeline.appliesTo]} reach
              {from && <StageChip family={pipeline.family} colour={from.colour} name={from.name} />}
              move the {NOUN[parent.appliesTo]} to
              {to && <StageChip family={parent.family} colour={to.colour} name={to.name} />}
              {canManage && (
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label="Remove rule"
                  onClick={() => remove.mutate(r.id)}
                >
                  <Trash2 />
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      {canManage && (
        <form
          className="flex flex-wrap items-center gap-2 text-sm"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          When
          <Select
            value={draft.condition}
            onValueChange={(v) => setDraft({ ...draft, condition: v as 'any' | 'all' })}
          >
            <SelectTrigger aria-label="Condition" className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">all</SelectItem>
              <SelectItem value="any">any</SelectItem>
            </SelectContent>
          </Select>
          reach
          <Select value={draft.source} onValueChange={(v) => setDraft({ ...draft, source: v })}>
            <SelectTrigger aria-label="Child stage" className="w-48">
              <SelectValue placeholder={`${pipeline.name}…`} />
            </SelectTrigger>
            <SelectContent>
              {pipeline.stages.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          move to
          <Select value={draft.target} onValueChange={(v) => setDraft({ ...draft, target: v })}>
            <SelectTrigger aria-label="Parent stage" className="w-48">
              <SelectValue placeholder={`${parent.name}…`} />
            </SelectTrigger>
            <SelectContent>
              {parent.stages.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" type="submit" disabled={!draft.source || !draft.target}>
            Add rule
          </Button>
        </form>
      )}
      {error && (
        <p className="text-danger text-sm" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

/** A stage row that is a drop target, dragged by its handle (pointer or keyboard). */
function StageRow({
  stage,
  canMove,
  busy,
  dragging,
  className,
  children,
}: {
  stage: PipelineStageView;
  canMove: boolean;
  busy: boolean;
  dragging: boolean;
  className: string;
  children: ReactNode;
}) {
  const drop = useDroppable({ id: stage.id });
  const drag = useDraggable({ id: stage.id, disabled: !canMove || busy });
  return (
    <li
      ref={drop.setNodeRef}
      data-testid="stage-row"
      data-stage-id={stage.id}
      className={cn(className, dragging && 'opacity-50', drop.isOver && !dragging && 'bg-muted')}
    >
      {canMove && (
        <button
          ref={drag.setNodeRef}
          type="button"
          {...drag.listeners}
          {...drag.attributes}
          aria-label={`Move ${stage.name}`}
          className="text-muted-foreground shrink-0 cursor-grab touch-none rounded-sm"
        >
          <GripVertical className="size-4" aria-hidden />
        </button>
      )}
      {children}
    </li>
  );
}

// ↑/↓ jump the dragged stage onto the row above or below.
const jumpBetweenRows: KeyboardCoordinateGetter = (event, { context, currentCoordinates }) => {
  if (event.code !== 'ArrowDown' && event.code !== 'ArrowUp') return undefined;
  const row = context.collisionRect;
  if (!row) return undefined;
  event.preventDefault();
  const centre = row.top + row.height / 2;
  const rects = context.droppableContainers
    .getEnabled()
    .map((c) => context.droppableRects.get(c.id))
    .filter((r): r is NonNullable<typeof r> => !!r)
    .sort((a, b) => a.top - b.top);
  const target =
    event.code === 'ArrowDown'
      ? rects.find((r) => r.top > centre)
      : [...rects].reverse().find((r) => r.top + r.height < centre);
  if (!target) return undefined;
  return { x: currentCoordinates.x, y: currentCoordinates.y + (target.top - row.top) };
};
